import { useSearchParams } from "react-router-dom"

import { useEvent } from "shared/lib/useEvent"
import { useRedux } from "shared/lib/useRedux"

import {
  UNASSIGNED_PRODUCTS_TAB_ID,
  UNASSIGNED_ROOMS_TAB_ID
} from "entities/project-children/lib/projectTabIDs"
import { SelectElementOptions } from "features/project-management/model/store/projectStoreActionHandlers"
import { useProjectStoreActions } from "features/project-management/model/store/useProjectStoreActions"

export function useProjectStore(projectID: string) {
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    collapsedRoomIDs,
    expandedProductPositionIDs,
    pendingElementIDs,
    selectedElementIDs,
    isDetailsViewOpen,
    sidebarIsEditing,
    sidebarActiveTab
  } = useRedux(({ table }) => ({
    isDetailsViewOpen: !!table[projectID]?.isDetailsViewOpen,
    collapsedRoomIDs: table[projectID]?.collapsedRoomIDs || [],
    expandedProductPositionIDs:
      table[projectID]?.expandedProductPositionIDs || [],
    pendingElementIDs: table[projectID]?.pendingElementIDs || [],
    selectedElementIDs: table[projectID]?.selectedElementIDs || [],
    sidebarIsEditing: !!table[projectID]?.sidebarIsEditing,
    sidebarActiveTab: table[projectID]?.sidebarActiveTab ?? null
  }))
  const actions = useProjectStoreActions()

  const activeTabID = searchParams.get("tab") ?? UNASSIGNED_PRODUCTS_TAB_ID
  const activeFloorID =
    activeTabID === UNASSIGNED_PRODUCTS_TAB_ID ||
    activeTabID === UNASSIGNED_ROOMS_TAB_ID
      ? null
      : activeTabID

  const collapseRoom = useEvent((roomID: string) => {
    actions.collapseRoom({ projectID, roomID })
  })

  const expandProductPosition = useEvent((productPositionID: string) => {
    actions.expandProductPosition({ projectID, productPositionID })
  })

  const collapseAll = useEvent((roomIDs: string[]) => {
    actions.collapseAll({ projectID, roomIDs })
  })

  const expandAll = useEvent((productPositionIDs: string[]) => {
    actions.expandAll({ projectID, productPositionIDs })
  })

  const selectElement = useEvent(
    (itemID: string | string[], options?: SelectElementOptions) => {
      actions.selectElement({ projectID, itemID, ...options })
    }
  )
  const openElementDetails = useEvent(() => {
    actions.openElementDetails({ projectID })
  })
  const closeElementDetails = useEvent(() => {
    actions.closeElementDetails({ projectID })
  })
  const unselectAll = useEvent(() => {
    actions.unselectAll({ projectID })
  })
  const enterSidebarEditing = useEvent(() => {
    actions.enterSidebarEditing({ projectID })
  })
  const exitSidebarEditing = useEvent(() => {
    actions.exitSidebarEditing({ projectID })
  })
  const setSidebarActiveTab = useEvent((tab: string | null) => {
    actions.setSidebarActiveTab({ projectID, tab })
  })

  const activateFloor = useEvent(
    (tabID: string | null, options?: { keepSelection?: boolean }) => {
      const resolvedTabID = tabID ?? UNASSIGNED_PRODUCTS_TAB_ID
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.set("tab", resolvedTabID)
          return next
        },
        { replace: true }
      )
      actions.activateFloor({
        projectID,
        tabID,
        keepSelection: options?.keepSelection
      })
    }
  )

  return {
    activateFloor,
    collapseRoom,
    expandProductPosition,
    collapseAll,
    expandAll,
    selectElement,

    openElementDetails,
    closeElementDetails,
    isDetailsViewOpen,

    unselectAll,
    activeTabID,
    activeFloorID,
    collapsedRoomIDs,
    expandedProductPositionIDs,
    selectedElementIDs,
    pendingElementIDs,

    sidebarIsEditing,
    sidebarActiveTab,
    enterSidebarEditing,
    exitSidebarEditing,
    setSidebarActiveTab
  }
}
